/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
    TextArea,
    Button,
    DividerNew as Divider,
    FlexRow,
    FlexCol,
    Label,
    useToast,
    announce,
} from 'sarsaparilla';
import { buildSearchNavUri, startAiSearch } from 'ui-search/src/actions/search';
import { SEARCH_INITIAL_STATE } from 'ui-search/src/constants';
import { getParsedAiSearchCriterias } from 'ui-search/src/utils/aiSearchUtils';

const exampleOptions = [
    'RV camping near Yellowstone with electric hookups',
    'Tent sites near Yosemite with hiking trails',
    'Camping near Grand Canyon for 4 people',
    'RV sites for a 30 foot RV near Glacier National Park',
    'Camping near Rocky Mountain National Park next month',
];

export default function HeroAiSearch() {
    const [searchLoading, setSearchLoading] = React.useState(false);
    const [startSearch, setStartSearch] = React.useState(false);
    const [iaTerm, setIaTerm] = React.useState('');
    const [error, setError] = React.useState('');

    const dispatch = useDispatch();
    const searchStore = useSelector((state) => state.search);
    const showToast = useToast();
    const textAreaRef = React.useRef();

    const navigateToSearch = () => {
        const url = buildSearchNavUri('/search', { search: searchStore });
        window.location.assign(url);
        setTimeout(() => {
            setSearchLoading(false);
            setStartSearch(false);
        }, 0);
    };

    React.useEffect(() => {
        if (startSearch) {
            setSearchLoading(false);
            navigateToSearch();
        }
        // eslint-disable-next-line
    }, [startSearch]);

    const triggerTextAreaError = () => {
        const message = 'Please add a search query';
        setError(message);
        const hasTextAreaRef = textAreaRef?.current?.focus;
        if (hasTextAreaRef) {
            textAreaRef.current.focus();
            announce(message);
        }
    };

    const submitSearch = async (value) => {
        const term = !value ? iaTerm : value;
        if (!term) {
            triggerTextAreaError();
            return;
        }
        setSearchLoading(true);
        try {
            const { data } = await axios.get(`${process.env.API}/search/parse?q=${term}`);
            // Use a clean uiFilters baseline — selector state can be bfcache-stale.
            const params = getParsedAiSearchCriterias(
                data,
                SEARCH_INITIAL_STATE.uiFilters
            );
            // resetSearchFilters() does not clear avail_months/dates. Replace from a
            // clean baseline so bfcache-revived Redux cannot leak prior filters;
            // getParsedAiSearchCriterias nulls the losing date mode itself.
            dispatch(startAiSearch(term, { ...SEARCH_INITIAL_STATE, ...params }));
            setStartSearch(true);
        } catch {
            setSearchLoading(false);
            showToast('There was an error, try again later.', {
                type: 'error',
                delay: 5000,
            });
        }
    };

    const onChange = (e) => {
        e.preventDefault();
        setIaTerm(e.target?.value);
        if (error) {
            setError('');
        }
    };

    const onExampleSelect = (v) => {
        setIaTerm(v);
        if (error) {
            setError('');
        }
        submitSearch(v);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitSearch();
        }
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        submitSearch();
    };

    return (
        <>
            <form
                onSubmit={handleSubmitForm}
                role="search"
                aria-label="AI Search"
                className="nav-search-input-wrapper nav-search-input-area-container "
            >
                <TextArea
                    className="nav-search-input-area"
                    placeholder="Tell us what kind of outdoor recreation you want to reserve. Be as descriptive as possible."
                    id="navigation-ia-area"
                    isLabelVisible={false}
                    onKeyDown={handleKeyDown}
                    value={iaTerm}
                    onChange={onChange}
                    innerRef={textAreaRef}
                    errorText={error}
                    isInvalid={error}
                />
                <Button
                    className="nav-search-button"
                    type="submit"
                    gaTrackingId="766083155256"
                    gaTrackingValue={`AI Search - ${iaTerm}`}
                >
                    SEARCH
                </Button>
            </form>

            <div>
                <Divider className="mt-3 mb-2" />
                <FlexRow
                    className="pb-1"
                    hasGutters={false}
                    justifyContent="space-between"
                >
                    <FlexCol>
                        <Label>Example Searches:</Label>
                    </FlexCol>
                </FlexRow>
                <FlexRow
                    alignItems="stretch"
                    hasGutters={false}
                    className="nav-search-example-boxes"
                >
                    {exampleOptions.map((item) => (
                        <FlexCol key={item}>
                            <Button
                                onClick={() => onExampleSelect(item)}
                                isDisabled={searchLoading}
                                size="sm"
                                isUnstyled
                                gaTrackingId="766083155256"
                                gaTrackingValue={`AI Search Sample - ${item}`}
                            >
                                <span className="nav-search-example-inner">{item}</span>
                            </Button>
                        </FlexCol>
                    ))}
                </FlexRow>
            </div>
        </>
    );
}
